import React from "react";
import { useSelector } from "react-redux";

import { HealthStateValue, type QueryView } from "~/models/dto";
import { Permitted } from "~/tools/permitted";

import { RightPaneState } from "../../../app/state/right-pane/RightPaneState";
import { selectViewSlug } from "../../../app/state/route/RouteSelectors";
import { isViewLockProtected, shouldHideFromOverview } from "../../../app/state/view/ViewUtils";
import { useCurrentInitialView, useIsCurrentViewDirty } from "../../../app/state/view/viewpoint/ViewPointHooks";
import { selectIsExploreSubview } from "../../../app/state/view/viewpoint/ViewPointSelectors";
import type { MainMenuViewItem } from "../../../common/openapi/models";
import { HealthStateCircle } from "../../../components/react/HealthCircle/HealthStateCircle";
import { useNavigateFromLink } from "../../../components/react/link";
import { ViewStar } from "../../../components/react/star/ViewStar";
import { useActiveMainMenuViewItem } from "../../../queries/mainmenu";
import { useGetViewTypeByIdentifier } from "../../../queries/viewtype";
import { VIEW_PATH } from "../../Routes";

import { GenericBreadcrumb } from "./GenericBreadcrumb";
import { SubviewBreadcrumb } from "./SubviewBreadcrumb";

export const ViewNameBreadcrumb = (): React.ReactNode => {
	const currentView = useCurrentInitialView({ isOriginView: true });
	const isSubview = useSelector(selectIsExploreSubview);
	const isViewDirty = useIsCurrentViewDirty({ isOriginView: true });
	const viewSlug = useSelector(selectViewSlug);
	const menuItem = useActiveMainMenuViewItem(viewSlug);
	const linkToView = useNavigateFromLink(VIEW_PATH, {
		params: (params) => (isSubview ? params : { ...params, mode: undefined }),
		search: (search) => {
			if (!isSubview) {
				return search;
			}

			// reset subview and restore the origin view/query
			return {
				...search,
				explore: undefined,
				view: search.originView,
				query: search.originQuery,
				rightPane: RightPaneState.resetWhenVisible(search.rightPane),
			};
		},
	});

	return Permitted.all({
		view: currentView,
		isDirty: isViewDirty,
	}).match({
		denied: () => null,

		granted: ({ value: { view, isDirty } }) => (
			<>
				<GenericBreadcrumb
					icon={<ViewBreadcrumbIcon menuItem={menuItem} view={view} />}
					isDirty={isDirty && !isViewLockProtected(view)}
					name={menuItem?.title ?? view.name}
					to={linkToView}
				>
					{/* relative raises the star over breadcrumb link so it becomes clickable */}
					{!shouldHideFromOverview(view) && <ViewStar className="relative -mr-1 ml-2.5" viewId={view.id!} />}
				</GenericBreadcrumb>

				{isSubview && <SubviewBreadcrumb />}
			</>
		),
	});
};

ViewNameBreadcrumb.PATH = [VIEW_PATH];

const ViewBreadcrumbIcon: React.FC<{
	menuItem: MainMenuViewItem | undefined;
	view: QueryView;
}> = ({ menuItem, view }) => {
	const { data: viewType } = useGetViewTypeByIdentifier(menuItem ? undefined : view.viewType);

	if (menuItem?.icon) {
		return <img alt={menuItem.title} className="max-w-none" src={menuItem.icon} />;
	}

	if (viewType?.iconbase64) {
		return <img alt={viewType.name} className="max-w-none" src={viewType.iconbase64} />;
	}

	return (
		<div className="relative" data-testid="views-detail-breadcrumb-health-state">
			<HealthStateCircle size="small" state={HealthStateValue.Uninitialized} />
		</div>
	);
};
